import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { arrowRightIcon } from '../../../icons';
import { CheckoutFlowService } from '../../checkout/checkout-flow.service';
import { CheckoutService } from '../../checkout/checkout.service';
import { SettingsService } from '../../core/settings.service';
import { LoanService } from '../loan.service';

@Component({
    selector: 'app-loan-details',
    template: `
        <ng-container *ngIf="loanService.loanData | async as loanData">
            <div
                class="loan-details"
                appCircleCheckmark
                [condition]="showCircleCheckmark">

                <div *ngIf="settingsService.get() | async as settingsData" class="loan-bank-info">
                    <div class="loan-bank-info__bank">
                        <img
                            loading="lazy"
                            class="loan-bank-info__logo"
                            [alt]="settingsData?.loanCalculator?.defaultBank?.name"
                            [title]="settingsData?.loanCalculator?.defaultBank?.name"
                            *ngIf="settingsData?.loanCalculator?.defaultBank?.logo?.src"
                            [src]="settingsData?.loanCalculator?.defaultBank?.logo?.src | resolveMediaUrl: { format: 'webp', width: 200 }"
                        />
                        <div class="loan-bank-info__bank-text">
                            <h6 *ngIf="settingsData?.loanCalculator?.defaultBank?.name?.length">
                                {{ settingsData?.loanCalculator?.defaultBank?.name }}
                            </h6>
                            <p *ngIf="settingsData?.loanCalculator?.defaultBank?.description?.length" class="text-small">
                                {{ settingsData?.loanCalculator?.defaultBank?.description }}
                            </p>
                        </div>
                    </div>
                    <div class="loan-bank-info__content">
                        <h5 *ngIf="loanData.paymentPerMonth">
                            <app-dynamic-value [isLoading]="!!(loanService.isLoading$ | async)">
                                <span class="loan__detail-value">{{ loanData.paymentPerMonth | defaultcurrency }}</span>
                                /{{ 'generic.month_abbreviation' | translate }}
                            </app-dynamic-value>
                        </h5>

                        <p class="no-margin text-small" *ngIf="loanData.downPayment">
                            {{ 'loan.down_payment' | translate }}:
                            <app-dynamic-value [isLoading]="!!(loanService.isLoading$ | async)">
                                <span class="loan__detail-value-wrapper bold">
                                    <span class="loan__detail-value">{{ loanData.downPayment | defaultcurrency }}</span>
                                </span>
                            </app-dynamic-value>
                        </p>

                        <p class="no-margin text-small" *ngIf="loanData.interestRate && loanData.nominalInterest">
                            {{ 'loan.interest_rate' | translate }}:
                            <app-dynamic-value [isLoading]="!!(loanService.isLoading$ | async)">
                                <span class="loan__detail-value-wrapper bold">
                                    <span class="loan__detail-value" [ngSwitch]="loanData?.interestType">
                                        <ng-container *ngSwitchCase="'Variabel'">{{ loanData.interestRate }}%</ng-container>
                                        <ng-container *ngSwitchDefault>{{ loanData.nominalInterest }}%</ng-container>
                                    </span>
                                </span>
                            </app-dynamic-value>
                        </p>
                    </div>
                </div>

                <div class="unstyled-table loan-table" role="table">
                    <div class="unstyled-table__row" role="row" (click)="toggleDetails()">
                        <div class="unstyled-table__cell unstyled-table__cell--header" role="columnheader">
                            <h6 class="link no-margin text-small">
                                <ng-container *ngIf="!showDetails"> {{ 'loan.details____show' | translate }}: </ng-container>
                                <ng-container *ngIf="showDetails"> {{ 'loan.details____hide' | translate }}: </ng-container>
                            </h6>
                        </div>
                    </div>

                    <ng-container *ngIf="showDetails">
                        <app-loan-details-table [loanData]="loanData"></app-loan-details-table>
                    </ng-container>
                </div>

                <div class="grid-spacing-normal no-margin-horizontally">
                    <app-rich-text cssClasses="rich-text--text-small" [html]="loanData.defaultDutyText"></app-rich-text>
                </div>
            </div>
            <div class="center grid-spacing-normal" *ngIf="showCompleteButton">
                <app-button
                    *ngIf="!(checkoutFlowService.atFlowStart$ | async)"
                    buttonClass="button--tertiary button--has-spacing button--has-bottom-spacing"
                    [title]="'checkout.button____go_back_to_previous_step' | translate"
                    (handleClick)="checkoutFlowService.prevStep()"
                ></app-button>
                <app-button
                    buttonClass="button--primary button--has-spacing button--has-bottom-spacing"
                    (handleClick)="handleLoanAccept(loanData)"
                    [loading]="!!(loanService.isLoading$ | async) || !!(checkoutService.isCheckoutLoading$ | async)"
                    [disabled]="!!(loanService.errorState$ | async)"
                    [title]="'loan.choose_and_submit_button' | translate"
                >
                    <i aria-hidden="true" class="button__icon">${arrowRightIcon}</i>
                </app-button>
            </div>
        </ng-container>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoanDetailsComponent {
    @Input() showCompleteButton = false;
    @Input() showCircleCheckmark = true;

    showDetails = false;

    constructor(
        public loanService: LoanService,
        public checkoutService: CheckoutService,
        public checkoutFlowService: CheckoutFlowService,
        readonly settingsService: SettingsService
    ) {}

    handleLoanAccept(currentLoan: any) {
        this.checkoutService.setLoanPayment(currentLoan);
    }

    toggleDetails() {
        this.showDetails = !this.showDetails;
    }
}
